import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { API_URL, SITE } from "@/lib/site";

const SUGGESTIONS = [
  "Combien coûte un devis ?",
  "Quels sont vos horaires ?",
  "C'est quoi l'offre hiver ?",
  "Faites-vous les pneus hiver ?",
  "Comment prendre rendez-vous ?",
  "Où êtes-vous exactement ?",
];

function getSessionId() {
  let sid = localStorage.getItem("bm_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("bm_chat_sid", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);
  const sidRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    sidRef.current = getSessionId();
    fetch(`${API_URL}/chat/history?session_id=${sidRef.current}`)
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy, open]);

  if (pathname.startsWith("/admin")) return null;

  const appendDelta = (delta) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
      return copy;
    });

  const setLastContent = (content) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content };
      return copy;
    });

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current || getSessionId(), message: content }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error("chat");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try {
            evt = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (evt.delta) appendDelta(evt.delta);
          else if (evt.error) setLastContent(evt.error);
        }
      }
    } catch {
      setLastContent(`Désolé, une erreur est survenue. Appelez-nous au ${SITE.phoneDisplay}.`);
    } finally {
      clearTimeout(timeout);
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed z-50 inset-x-3 bottom-36 sm:inset-x-auto sm:right-6 sm:w-[380px] md:bottom-24 flex flex-col bg-[#121212] border border-[#33353B] shadow-2xl h-[55vh] max-h-[520px]"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#33353B]">
            <div>
              <p className="font-display font-bold uppercase tracking-tight text-sm text-[#F6F3EB]">
                Assistant BM Mechanics
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C7B5D] mt-0.5">
                Réponses instantanées — assistant automatique
              </p>
            </div>
            <button
              data-testid="chat-widget-close-btn"
              aria-label="Fermer le chat"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center h-8 w-8 border border-[#33353B] text-[#9A968C] hover:text-[#F6F3EB] transition-colors shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div data-testid="chat-widget-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="max-w-[85%] bg-[#1E2024] text-[#F6F3EB] px-3.5 py-2.5 text-sm leading-relaxed">
                Bonjour ! Une question sur nos services, nos horaires ou l'offre hiver ? Je vous réponds tout de suite.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${i}`}
                className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#8C7B5D] text-[#121212]"
                    : "bg-[#1E2024] text-[#F6F3EB]"
                }`}
              >
                {m.content === "" && m.role === "assistant" ? (
                  <span className="inline-flex gap-1 py-1" aria-label="L'assistant écrit">
                    <span className="h-1.5 w-1.5 bg-[#8C7B5D] animate-pulse" />
                    <span className="h-1.5 w-1.5 bg-[#8C7B5D] animate-pulse [animation-delay:150ms]" />
                    <span className="h-1.5 w-1.5 bg-[#8C7B5D] animate-pulse [animation-delay:300ms]" />
                  </span>
                ) : (
                  m.content
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {messages.length === 0 && (
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  data-testid={`chat-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="shrink-0 border border-[#33353B] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[#9A968C] hover:text-[#F6F3EB] hover:border-[#8C7B5D] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            data-testid="chat-widget-form"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-[#33353B] p-3"
          >
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Votre question…"
              maxLength={1000}
              className="flex-1 bg-[#1E2024] border border-[#33353B] px-3.5 py-2.5 text-sm text-[#F6F3EB] placeholder:text-[#4A4D53] focus:outline-none focus:border-[#8C7B5D] transition-colors"
            />
            <button
              type="submit"
              data-testid="chat-widget-send-btn"
              aria-label="Envoyer"
              disabled={busy || !input.trim()}
              className="inline-flex items-center justify-center h-10 w-10 bg-[#8C7B5D] text-[#121212] hover:bg-[#9c8a6b] transition-colors disabled:opacity-40 shrink-0"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
          <div className="border-t border-[#33353B] px-4 py-2.5 text-center">
            <Link
              to="/devis"
              data-testid="chat-widget-devis-link"
              onClick={() => setOpen(false)}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#8C7B5D] hover:text-[#F6F3EB] transition-colors"
            >
              Devis en 2 minutes — CHF 30.- déduits des travaux
            </Link>
          </div>
        </div>
      )}

      <button
        data-testid="chat-widget-open-btn"
        aria-label="Ouvrir le chat BM Mechanics"
        onClick={() => setOpen((o) => !o)}
        className="fixed z-50 bottom-20 right-4 md:bottom-6 md:right-6 inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#8C7B5D] text-[#121212] shadow-[0_8px_24px_rgba(0,0,0,0.4)] hover:bg-[#9c8a6b] hover:scale-105 transition-all"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>
    </>
  );
}
